import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowRight, Loader2, Check } from "lucide-react";
import { useDocMeta } from "../../lib/useDocMeta";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function CustomerLogin() {
  useDocMeta({ title: "Customer Login · GovBidIQ", description: "Sign in to your GovBidIQ customer dashboard via magic link.", path: "/customer/login" });
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const { loginWithSession } = useCustomerAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/customer/auth/request`, { email: email.trim().toLowerCase() });
      setSent(true);
      // Dev convenience: surface the verify link if backend ran in dev mode
      if (r.data.verify_url) setDevLink(r.data.dev_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send magic link");
    } finally { setBusy(false); }
  };

  const verifyDev = async (token) => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/customer/auth/verify`, { token });
      loginWithSession(r.data.session_token, r.data.customer);
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--bg-primary)]">
      <div className="w-full max-w-md border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-8">
        <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Customer Sign-In</div>
        <h1 className="font-display font-black text-2xl text-slate-100 mb-3">Welcome back.</h1>
        <p className="text-sm text-slate-400 mb-6 leading-relaxed">Enter your email — we&apos;ll send you a magic sign-in link. No password.</p>

        {!sent ? (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">Email</div>
              <input type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" data-testid="cust-login-email" className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2.5 font-mono" />
            </label>
            <button type="submit" disabled={busy} data-testid="cust-login-submit" className="w-full bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send magic link
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-3 flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.5} />
              <div className="text-sm text-slate-300 leading-relaxed">Check your inbox for a sign-in link. It expires in 15 minutes.</div>
            </div>
            {devLink && (
              <div className="border border-amber-500/40 bg-amber-500/5 p-3">
                <div className="font-mono text-[10px] tracking-widest uppercase text-amber-300 mb-1">/ Dev Mode</div>
                <p className="text-xs text-slate-400 mb-2">Email delivery isn&apos;t wired yet — click below to sign in directly.</p>
                <button onClick={() => verifyDev(devLink)} data-testid="cust-login-dev-verify" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-widest uppercase px-3 py-2 inline-flex items-center gap-1.5">
                  Sign in now <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            )}
            <button onClick={() => { setSent(false); setDevLink(null); }} className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400">← Use a different email</button>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-[var(--border-soft)] text-xs text-slate-500 leading-relaxed">
          Not a customer yet? <Link to="/discovery" className="text-sky-400 hover:text-sky-300">Start AI Discovery →</Link>
        </div>
      </div>
    </div>
  );
}

// Magic-link verify route handler — reads ?token=… from URL and exchanges.
export function CustomerVerify() {
  const { loginWithSession } = useCustomerAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("verifying");
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setStatus("error"); return; }
    axios.post(`${BASE}/api/customer/auth/verify`, { token })
      .then((r) => { loginWithSession(r.data.session_token, r.data.customer); nav("/app/dashboard"); })
      .catch(() => setStatus("error"));
  }, [loginWithSession, nav]);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-300">
      {status === "verifying" ? <Loader2 className="w-5 h-5 animate-spin" /> :
        <div className="text-center">
          <div className="font-display font-bold text-slate-100 text-lg mb-2">Link expired or invalid</div>
          <Link to="/customer/login" className="font-mono text-[10px] tracking-widest uppercase text-sky-400">Request a new link →</Link>
        </div>}
    </div>
  );
}
